import type { Editor } from "@tiptap/react";
import type { Asset } from "schemas/generated/asset";

import * as React from "react";

import { DebouncedInput } from "@editor/components/common/designSystem/Input";
import useCurrentProjectId from "@editor/hooks/useCurrentProjectId";
import { assetRouter } from "@editor/trpc/asset";

import { AssetsGrid } from "@/features/assets/components/AssetsGrid";
import { selectAsset } from "@/features/react-editor/components/chat/utils/contextItem";
import { skipToken } from "@tanstack/react-query";
import { Search } from "lucide-react";

export const InspirationMenu: React.FC<{
  editor: Editor | null;
  onClose?: () => void;
}> = ({ editor, onClose }) => {
  const [searchQuery, setSearchQuery] = React.useState("");
  const projectId = useCurrentProjectId();

  const {
    data,
    isLoading: isAssetsLoading,
    fetchNextPage,
    hasNextPage,
  } = assetRouter.list.useInfiniteQuery(
    projectId
      ? {
          projectId,
          types: ["image"],
          limit: 16,
          search: searchQuery,
          scope: "global",
        }
      : skipToken,
    {
      getNextPageParam: ({ nextCursor }) => nextCursor,
    },
  );

  const assets = React.useMemo(
    () => data?.pages?.flatMap((page) => page.assets) ?? [],
    [data],
  );

  const handleAssetSelect = (asset: Asset) => {
    selectAsset(asset, editor);
    onClose?.();
  };

  return (
    <div className="p-3 space-y-3 h-[400px]">
      <DebouncedInput
        placeholder="Search sections..."
        startEnhancer={<Search size={14} />}
        size="sm"
        value={searchQuery}
        onValueChange={setSearchQuery}
        autoFocus={true}
      />

      <div className="h-full flex flex-col">
        <AssetsGrid
          assets={assets}
          searchTerm={searchQuery}
          isLoading={isAssetsLoading}
          hasNextPage={hasNextPage}
          fetchNextPage={() => void fetchNextPage()}
          emptyState={<div>No assets found</div>}
          onAssetSelected={handleAssetSelect}
        />
      </div>
    </div>
  );
};
